<template>
  <div id="app"
    class="App"
    :class="appClasses"
    :style="appStyles">

    <overlay-menu />

    <router-view class="App-content"></router-view>

    <share-modal />

    <viewer />

    <!-- <vue-progress-bar /> -->

  </div>
</template>

<script>
import { concat, throttle } from 'lodash'
import smoothscroll from 'smoothscroll'

export default {
  name: 'App',

  metaInfo () {
    return {
      title: this.page.attributes.title,
      titleTemplate: title => {
        return title ? `${title} | ${this.site.title}` : this.site.title
      },
      meta: concat(this.meta, this.imageMeta)
    }
  },

  data () {
    return {
      scrollLock: false,
      lastWindowY: 0,
      imageMeta: []
    }
  },

  computed: {
    site () { return this.$store.state.data.site },
    viewer () { return this.$store.state.viewer },
    share () { return this.$store.state.share },
    scrollToTop () { return this.$store.state.window.scrollToTop },
    preventScroll () { return this.$store.state.window.preventScroll },

    /**
     * Classes for the main element
     * @return {Object}
     */
    appClasses () {
      return {
        'App--preventScroll': this.scrollLock,
        'App--viewerIsOpen': this.viewer.isOpen,
        'App--shareModalIsOpen': this.share.modalIsOpen
      }
    },

    /**
     * Styles for the main element
     * @return {Object}
     */
    appStyles () {
      const styles = {}

      if (this.scrollLock) {
        styles['top'] = `${-window.scrollY}px`
      } else {
        styles['top'] = 'auto'
      }

      return styles
    },

    /**
     * Meta objects for <head>
     * - https://developers.facebook.com/docs/sharing/webmasters#basic
     * - Setting a 'vmid' on each object allows them to be overridden in components.
     * - Image meta tags are handled in app/src/plugins/page.js or individual components.
     *
     * @return {Array}
     */
    meta () {
      const meta = []

      // In order to use Facebook Insights you must add the app ID to your page. Insights lets you view analytics for traffic to your site from Facebook. Find the app ID in your App Dashboard.
      if (this.site.facebookAppId) {
        meta.push({
          vmid: 'fb:app_id',
          property: 'fb:app_id',
          content: this.site.facebookAppId
        })
      }

      // The canonical URL for your page. This should be the undecorated URL, without session variables, user identifying parameters, or counters. Likes and Shares for this URL will aggregate at this URL. For example, mobile domain URLs should point to the desktop version of the URL as the canonical URL to aggregate Likes and Shares across different versions of the page.
      meta.push({
        vmid: 'og:url',
        property: 'og:url',
        content: `${this.site.baseUrl}${this.$route.fullPath}`
      })

      // The title of your article without any branding such as your site name.
      meta.push({
        vmid: 'og:title',
        property: 'og:title',
        content: this.page.attributes.title || this.site.title
      })

      // https://developers.facebook.com/docs/reference/opengraph#object-type
      meta.push({
        vmid: 'og:type',
        property: 'og:type',
        content: 'website'
      })

      // A brief description of the content, usually between 2 and 4 sentences. This will displayed below the title of the post on Facebook.
      if (this.page.attributes.description || this.page.attributes.excerpt) {
        meta.push({
          vmid: 'og:description',
          property: 'og:description',
          content: this.page.attributes.description || this.page.attributes.excerpt
        })

        meta.push({
          vmid: 'description',
          name: 'description',
          content: this.page.attributes.description || this.page.attributes.excerpt
        })
      }

      return meta
    }
  },

  created () {
    // Create throttled event handlers
    this.throttledHandleResize = throttle(this.handleResize, 250)
    this.throttledHandleScroll = throttle(this.handleScroll, 50)

    this.addEventListeners()
    this.handleResize()

    // this.$Progress.start()
  },

  mounted () {
    // this.$Progress.finish()
  },

  watch: {
    scrollToTop () {
      if (!this.scrollToTop) return
      smoothscroll(document.querySelector('body'))
    },

    preventScroll () {
      if (this.preventScroll) {
        this.lastWindowY = window.scrollY
        this.$nextTick(() => {
          this.scrollLock = true
        })
        // window.addEventListener('touchmove', this.handleScroll)
      } else {
        this.scrollLock = false
        this.$nextTick(() => {
          window.scrollTo(0, this.lastWindowY)
        })
        // window.removeEventListener('touchmove', this.handleScroll)
      }
    },

    /**
     * Make sure preventScroll is reset on route change
     */
    '$route.path' () {
      this.$store.dispatch('window/setPreventScroll', false)
    },

    'page': 'setImageMeta'
  },

  methods: {
    /**
     * Add event listeners
     */
    addEventListeners () {
      window.addEventListener('resize', this.throttledHandleResize)
      window.addEventListener('scroll', this.throttledHandleScroll)
    },

    /**
     * Handle window resize event
     */
    handleResize () {
      this.$store.dispatch('window/setWidth', window.innerWidth)
      this.$store.dispatch('window/setHeight', window.innerHeight)
    },

    /**
     * Handle window scroll event
     * @param  {Object} event
     */
    handleScroll (event) {
      this.$store.dispatch('window/setScrollY', window.scrollY)
    },

    async setImageMeta () {
      const image = this.page.attributes.image

      // Reset image meta
      this.imageMeta = []

      // Exit if no image
      if (!image) return

      // Set normal image
      if (image.src) {
        this.$set(this.imageMeta, this.imageMeta.length, {
          vmid: 'og:image',
          property: 'og:image',
          content: image.src
        })

        return
      }

      const data = await this.$smugmug.get(this.page.attributes.image.smugmug)
      const width = 1200
      const height = this.$smugmug.getHeight(data, width)

      this.$set(this.imageMeta, this.imageMeta.length, {
        vmid: 'og:image',
        property: 'og:image',
        content: this.$smugmug.resolveTemplate(data, width, height)
      })

      this.$set(this.imageMeta, this.imageMeta.length, {
        vmid: 'og:image:width',
        property: 'og:image:width',
        content: width
      })

      this.$set(this.imageMeta, this.imageMeta.length, {
        vmid: 'og:image:height',
        property: 'og:image:height',
        content: height
      })
    }
  }
}
</script>

<style>
@import '../../node_modules/normalize.css/normalize';
@import '../../node_modules/suitcss-base/lib/base.css';

@import 'properties';
@import 'layout';
@import 'colors';
@import 'themes';
@import 'fonts';
@import 'type';

.App--preventScroll {
  position: fixed;
  right: 0;
  left: 0;
  /*overflow-y: hidden;*/
  /*overflow: auto;*/
  /*height: 100vh;*/
}

.App-content {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  /* background: var(--c-white); */
}

/* body::before {
    content: '';
    position: fixed;
    top: -50%;
    right: -50%;
    bottom: -50%;
    left: -50%;
    z-index: -100;
    background-image: linear-gradient(to bottom,
      var(--c-white) 0,
      var(--c-white) 50%,
      var(--c-grey-dk) 50%,
      var(--c-grey-dk) 100%);
} */
</style>



// WEBPACK FOOTER //
// App.vue?c1dea032